import { ApiEnums } from '../Interfaces/ApiEnums';
import {
  HEAVY_WEIGHT_CATEGORY,
  LIGHT_WEIGHT_CATEGORY,
  MEDIUM_WEIGHT_CATEGORY,
  MIXED_WEIGHT_CATEGORY,
} from './constants';

import { GetString } from '../components/StringResourceManager';

import StringResources from './stringResources';

// Alert Types
var alertTypeMap = new Map([
  [ApiEnums.AlertType.MixedLightHeavyWeights, 'MIXED_LIGHT_HEAVY_STR'],
  [ApiEnums.AlertType.MixedZeroHeavyWeights, 'MIXED_ZERO_HEAVY_STR'],
  [ApiEnums.AlertType.WeightOverridden, 'WEIGHT_OVERRIDDEN_STR'],
  [ApiEnums.AlertType.Unknown, 'UNKNOWN_STR'],
]);

// Device Statuses
var deviceStatusMap = new Map([
  [ApiEnums.DeviceStatus.Ok, 'OK_BUTTON'],
  [ApiEnums.DeviceStatus.Inconvenienced, 'WARNING'],
  [ApiEnums.DeviceStatus.Error, 'ERROR'],
  [ApiEnums.DeviceStatus.Unknown, 'UNKNOWN_STR'],
]);

// Registration Status
var registrationStatusMap = new Map([
  [ApiEnums.ManagementActions.Active, 'ACTIVE'],
  [ApiEnums.ManagementActions.Decommissioned, 'DECOMMISSIONED'],
  [ApiEnums.ManagementActions.ReimagePending, 'REIMAGE_PENDING'],
  [ApiEnums.ManagementActions.TransferPending, 'TRANSFER_PENDING'],
]);

export const alertTypeNameMap = (alertType: ApiEnums.AlertType) => {
  return alertTypeMap.get(alertType);
};

export const deviceStatusesNameMap = (
  status: ApiEnums.DeviceStatus,
): string | undefined => {
  return deviceStatusMap.get(status);
};

export const registrationStatusesNameMap = (
  status: ApiEnums.ManagementActions,
) => {
  return registrationStatusMap.get(status);
};

function getAlertTypeKeyByValue(value: any) {
  if (isMatch('MIXED_LIGHT_HEAVY_STR', value)) return 'MIXED_LIGHT_HEAVY_STR';
  else if (isMatch('MIXED_ZERO_HEAVY_STR', value))
    return 'MIXED_ZERO_HEAVY_STR';
  else if (isMatch('WEIGHT_OVERRIDDEN_STR', value))
    return 'WEIGHT_OVERRIDDEN_STR';

  return 'UNKNOWN_STR';
}

export function isMatch(key: string, value: string) {
  return (
    StringResources.English[key as keyof typeof StringResources.English] ===
      value ||
    StringResources.French[key as keyof typeof StringResources.French] === value
  );
}

export function getDeviceStatusKeyByValue(value: any) {
  if (isMatch('OK_BUTTON', value)) return 'OK_BUTTON';
  else if (isMatch('WARNING', value)) return 'WARNING';
  else if (isMatch('ERROR', value)) return 'ERROR';
  return 'UNKNOWN_STR';
}

export function getRegistrationStatusKeyByValue(value: any) {
  if (isMatch('ACTIVE', value)) return 'ACTIVE';
  else if (isMatch('DECOMMISSIONED', value)) return 'DECOMMISSIONED';
  else if (isMatch('REIMAGE_PENDING', value)) return 'REIMAGE_PENDING';
  return 'TRANSFER_PENDING';
}

export const alertTextToTypeMap = (value: any) => {
  let keyFound: ApiEnums.AlertType | undefined = Array.from(
    alertTypeMap.keys(),
  ).find(key => alertTypeMap.get(key) === getAlertTypeKeyByValue(value));
  return keyFound !== undefined ? keyFound : '';
};

export const statusTextToTypeMap = (value: any) => {
  let keyFound: ApiEnums.DeviceStatus | undefined = Array.from(
    deviceStatusMap.keys(),
  ).find(key => deviceStatusMap.get(key) === getDeviceStatusKeyByValue(value));
  return keyFound !== undefined ? keyFound : '';
};

export const registrationStatusTextToTypeMap = (value: any) => {
  let keyFound: ApiEnums.ManagementActions | undefined = Array.from(
    registrationStatusMap.keys(),
  ).find(
    key =>
      registrationStatusMap.get(key) === getRegistrationStatusKeyByValue(value),
  );
  return keyFound !== undefined ? keyFound : '';
};

export const getAlertTextArray = (): Array<string> => {
  const alertTypeValues = Array.from(alertTypeMap.values());
  var localizedAlertTypeValues: string[] = [];

  alertTypeValues.forEach(function (value: any, key: any, map: any) {
    localizedAlertTypeValues.push(GetString(value));
  });

  return localizedAlertTypeValues;
};

export const getDeviceStatusTextArray = (): Array<string> => {
  const deviceStatusValues = Array.from(deviceStatusMap.values());
  var localizedStatusTypeValues: string[] = [];

  deviceStatusValues.forEach(function (value: any, key: any, map: any) {
    localizedStatusTypeValues.push(GetString(value));
  });

  return localizedStatusTypeValues;
};

export const getRegistrationStatusTextArray = (): Array<string> => {
  const registrationStatusValues = Array.from(registrationStatusMap.values());
  var localizedStatusTypeValues: string[] = [];

  registrationStatusValues.forEach(function (value: any, key: any, map: any) {
    localizedStatusTypeValues.push(GetString(value));
  });

  return localizedStatusTypeValues;
};

// Alert Category
export const getWeightCategoryText = (
  weightCategory: ApiEnums.WeightCategory,
): string => {
  switch (weightCategory) {
    case ApiEnums.WeightCategory.Mixed:
      return MIXED_WEIGHT_CATEGORY;
    case ApiEnums.WeightCategory.Light:
      return LIGHT_WEIGHT_CATEGORY;
    case ApiEnums.WeightCategory.Medium:
      return MEDIUM_WEIGHT_CATEGORY;
    case ApiEnums.WeightCategory.Heavy:
      return HEAVY_WEIGHT_CATEGORY;
    default:
      break;
  }

  return MIXED_WEIGHT_CATEGORY;
};

// Supported Commands
const supportedCommandTextMap = new Map([
  [ApiEnums.AlertCommand.UpdateWeightRecord, 'UPDATE_STR'],
  [ApiEnums.AlertCommand.CleanWeightRecord, 'CLEAN_STR'],
  [ApiEnums.AlertCommand.SetWeightRecordToDefault, 'SET_TO_DEFAULT_STR'],
  [ApiEnums.AlertCommand.SetWeightThreshold, 'SET_TRESHOLD'],
  [ApiEnums.AlertCommand.CloseAlert, 'CLOSE_STR'],
  [ApiEnums.AlertCommand.ApplyRecommended, 'APPLY_RECOMMENDED'],
]);

export const commandNameMap = (command: ApiEnums.AlertCommand): string => {
  return supportedCommandTextMap.get(command) ?? '';
};

export const getCommandNameOptions = (): Array<string> => {
  return Array.from(supportedCommandTextMap.values());
};

function getCommandStatusKeyByValue(value: any) {
  if (isMatch('NONE_STR', value)) return 'NONE_STR';
  else if (isMatch('NEW_STR', value)) return 'NEW_STR';
  else if (isMatch('IN_PROGRESS_TITLE', value)) return 'IN_PROGRESS_TITLE';
  else if (isMatch('COMPLETED_STR', value)) return 'COMPLETED_STR';
  else if (isMatch('COMPLETED_WITH_ERROR', value))
    return 'COMPLETED_WITH_ERROR';
  else if (isMatch('FAILED_STR', value)) return 'FAILED_STR';
  else if (isMatch('PENDING_STR', value)) return 'PENDING_STR';
  else if (isMatch('EXECUTION_IN_PROGRESS', value))
    return 'EXECUTION_IN_PROGRESS';
  else if (isMatch('EXECUTION_COMPLETED', value)) return 'EXECUTION_COMPLETED';
  else if (isMatch('EXECUTION_COMPLETED_WITH_ERRORS', value))
    return 'EXECUTION_COMPLETED_WITH_ERRORS';
  else if (isMatch('EXECUTION_FAILED', value)) return 'EXECUTION_FAILED';

  return 'CANCELED_STR';
}
// Command Statuses
const commandStatusMap = new Map([
  [ApiEnums.CommandStatus.New, 'NEW_STR'],
  [ApiEnums.CommandStatus.Pending, 'PENDING_STR'],
  [ApiEnums.CommandStatus.InProgress, 'IN_PROGRESS_TITLE'],
  [ApiEnums.CommandStatus.Completed, 'COMPLETED_STR'],
  [ApiEnums.CommandStatus.CompletedWithErrors, 'COMPLETED_WITH_ERROR'],
  [ApiEnums.CommandStatus.Failed, 'FAILED_STR'],
  [ApiEnums.CommandStatus.Canceled, 'CANCELED_STR'],
  [ApiEnums.CommandStatus.None, 'NONE_STR'],
]);

const commandStatusStringMap = new Map<string, string>([
  [ApiEnums.CommandStatus[ApiEnums.CommandStatus.New], 'NEW_STR'],
  [ApiEnums.CommandStatus[ApiEnums.CommandStatus.Pending], 'PENDING_STR'],
  [
    ApiEnums.CommandStatus[ApiEnums.CommandStatus.InProgress],
    'IN_PROGRESS_TITLE',
  ],
  [ApiEnums.CommandStatus[ApiEnums.CommandStatus.Completed], 'COMPLETED_STR'],
  [
    ApiEnums.CommandStatus[ApiEnums.CommandStatus.CompletedWithErrors],
    'COMPLETED_WITH_ERROR',
  ],
  [ApiEnums.CommandStatus[ApiEnums.CommandStatus.Failed], 'FAILED_STR'],
  [ApiEnums.CommandStatus[ApiEnums.CommandStatus.Canceled], 'CANCELED_STR'],
  [ApiEnums.CommandStatus[ApiEnums.CommandStatus.None], 'NONE_STR'],
]);

const scheduledCommandStatusMap = new Map([
  [ApiEnums.ScheduledCommandStatus.Pending, 'PENDING_STR'],
  [
    ApiEnums.ScheduledCommandStatus.ExecutionInProgress,
    'EXECUTION_IN_PROGRESS',
  ],
  [ApiEnums.ScheduledCommandStatus.ExecutionCompleted, 'EXECUTION_COMPLETED'],
  [
    ApiEnums.ScheduledCommandStatus.ExecutionCompletedWithErrors,
    'EXECUTION_COMPLETED_WITH_ERRORS',
  ],
  [ApiEnums.ScheduledCommandStatus.ExecutionFailed, 'EXECUTION_FAILED'],
  [ApiEnums.ScheduledCommandStatus.Completed, 'COMPLETED_STR'],
  [ApiEnums.ScheduledCommandStatus.CompletedWithErrors, 'COMPLETED_WITH_ERROR'],
  [ApiEnums.ScheduledCommandStatus.Failed, 'FAILED_STR'],
  [ApiEnums.ScheduledCommandStatus.Canceled, 'CANCELED_STR'],
  [ApiEnums.ScheduledCommandStatus.None, 'NONE_STR'],
]);

export const getCommandStatusOptions = (): Array<string> => {
  const commandStatusOptions = Array.from(commandStatusMap.values());
  var localizedCommandStatusOptions: string[] = [];

  commandStatusOptions.forEach(function (value: any, key: any, map: any) {
    localizedCommandStatusOptions.push(GetString(value));
  });

  return localizedCommandStatusOptions;
};

export const commandStatusTextMap = (key: ApiEnums.CommandStatus): string => {
  let value = commandStatusMap.get(key);
  return value ? value : '';
};

export const commandStatusStringTextMap = (key: string): string => {
  let value = commandStatusStringMap.get(key);
  return value ? value : '';
};

export const commandStatusToTypeMap = (value: any) => {
  let keyFound: ApiEnums.CommandStatus | undefined = Array.from(
    commandStatusMap.keys(),
  ).find(
    key => commandStatusMap.get(key) === getCommandStatusKeyByValue(value),
  );
  return keyFound !== undefined ? keyFound : '';
};

export const commandStatusStringToTypeMap = (value: any) => {
  let keyFound: string | undefined = Array.from(
    commandStatusStringMap.keys(),
  ).find(
    key =>
      commandStatusStringMap.get(key) === getCommandStatusKeyByValue(value),
  );
  return keyFound !== undefined ? keyFound : '';
};

export const getScheduledCommandStatusOptions = (): Array<string> => {
  const scheduledCommandStatusOptions = Array.from(
    scheduledCommandStatusMap.values(),
  );
  var localizedCommandStatusOptions: string[] = [];

  scheduledCommandStatusOptions.forEach(function (
    value: any,
    key: any,
    map: any,
  ) {
    localizedCommandStatusOptions.push(GetString(value));
  });

  return localizedCommandStatusOptions;
};

export const scheduledCommandStatusTextMap = (
  key: ApiEnums.ScheduledCommandStatus,
): string => {
  let value = scheduledCommandStatusMap.get(key);
  return value ? value : '';
};

export const scheduledCommandStatusToTypeMap = (value: any) => {
  let keyFound: ApiEnums.ScheduledCommandStatus | undefined = Array.from(
    scheduledCommandStatusMap.keys(),
  ).find(
    key =>
      scheduledCommandStatusMap.get(key) === getCommandStatusKeyByValue(value),
  );
  return keyFound !== undefined ? keyFound : '';
};

function getIncidentStatusKeyByValue(value: any) {
  if (isMatch('UNKNOWN_STR', value)) return 'UNKNOWN_STR';
  else if (isMatch('OPEN_STR', value)) return 'OPEN_STR';
  else if (isMatch('IN_PROGRESS_TITLE', value)) return 'IN_PROGRESS_TITLE';
  else if (isMatch('RESOLVED_STR', value)) return 'RESOLVED_STR';

  return 'CLOSED_STR';
}

// Incident Statuses
const incidentStatusMap = new Map([
  [ApiEnums.IncidentStatus.Unknown, 'UNKNOWN_STR'],
  [ApiEnums.IncidentStatus.Open, 'OPEN_STR'],
  [ApiEnums.IncidentStatus.InProgress, 'IN_PROGRESS_TITLE'],
  [ApiEnums.IncidentStatus.Resolved, 'RESOLVED_STR'],
  [ApiEnums.IncidentStatus.Closed, 'CLOSED_STR'],
]);

export const getIncidentStatusOptions = (): Array<string> => {
  const incidentStatus = Array.from(incidentStatusMap.values());
  var localizedIncidentStatus: string[] = [];

  incidentStatus.forEach(function (value: any, key: any, map: any) {
    localizedIncidentStatus.push(GetString(value));
  });

  return localizedIncidentStatus;
};

export const incidentStatusTextMap = (key: ApiEnums.IncidentStatus): string => {
  let value = incidentStatusMap.get(key);
  return value ? value : '';
};

export const incidentStatusToTypeMap = (value: any) => {
  let keyFound: ApiEnums.IncidentStatus | undefined = Array.from(
    incidentStatusMap.keys(),
  ).find(
    key => incidentStatusMap.get(key) === getIncidentStatusKeyByValue(value),
  );
  return keyFound !== undefined ? keyFound : '';
};

export const supportedTicketCommandTextMap = new Map([
  [ApiEnums.TicketCommand.CloseTicket, 'CLOSE_TICKET'],
  [ApiEnums.TicketCommand.ResetDevice, 'COMMAND_RESET_DEVICE'],
  [ApiEnums.TicketCommand.Reboot, 'COMMAND_REBOOT_LANE'],
]);

export const getSupportedTicketCommands = (): Array<string> => {
  const incidentStatus = Array.from(supportedTicketCommandTextMap.values());
  var localizedTicketCommands: string[] = [];

  incidentStatus.forEach(function (value: any, key: any, map: any) {
    localizedTicketCommands.push(GetString(value));
  });

  return localizedTicketCommands;
};

export const ticketCommandNameMap = (
  command: ApiEnums.TicketCommand,
): string => {
  return supportedTicketCommandTextMap.get(command) ?? '';
};

// Preventive Maintenance (DMM) Statuses
function getPrevMaintenanceStatusKeyByValue(value: any) {
  if (isMatch('OPEN_STR', value)) return 'OPEN_STR';
  else if (isMatch('TECH_DISPATCH_REQUESTED', value))
    return 'TECH_DISPATCH_REQUESTED';
  else if (isMatch('TECH_DISPATCHED', value)) return 'TECH_DISPATCHED';
  else if (isMatch('CLOSED_STR', value)) return 'CLOSED_STR';
  else if (isMatch('DONE_BY_TECH_ENTERPRISE', value))
    return 'DONE_BY_TECH_ENTERPRISE';
  else if (isMatch('DONE_BY_TECH_DP', value)) return 'DONE_BY_TECH_DP';
  else if (isMatch('DONE_AUTOMATICALLY', value)) return 'DONE_AUTOMATICALLY';

  return 'UNKNOWN_STR';
}

const prevMaintenanceStatusMap = new Map([
  [ApiEnums.PreventiveMaintenanceStatus.Unknown, 'UNKNOWN_STR'],
  [ApiEnums.PreventiveMaintenanceStatus.Open, 'OPEN_STR'],
  [
    ApiEnums.PreventiveMaintenanceStatus.TechDispatchRequested,
    'TECH_DISPATCH_REQUESTED',
  ],
  [ApiEnums.PreventiveMaintenanceStatus.TechDispatched, 'TECH_DISPATCHED'],
  [
    ApiEnums.PreventiveMaintenanceStatus.DoneByTechEnterprise,
    'DONE_BY_TECH_ENTERPRISE',
  ],
  [ApiEnums.PreventiveMaintenanceStatus.DoneByTechDP, 'DONE_BY_TECH_DP'],
  [
    ApiEnums.PreventiveMaintenanceStatus.DoneAutomatically,
    'DONE_AUTOMATICALLY',
  ],
]);

export const getPrevMaintenanceStatusOptions = (): Array<string> => {
  const status = Array.from(prevMaintenanceStatusMap.values());
  var localizedStatus: string[] = [];

  status.forEach(function (value: any, key: any, map: any) {
    localizedStatus.push(GetString(value));
  });

  return localizedStatus;
};

export const prevMaintenanceStatusTextMap = (
  key: ApiEnums.PreventiveMaintenanceStatus,
): string => {
  let value = prevMaintenanceStatusMap.get(key);
  return value ? value : '';
};

export const prevMaintenanceStatusToTypeMap = (value: any) => {
  let keyFound: ApiEnums.PreventiveMaintenanceStatus | undefined = Array.from(
    prevMaintenanceStatusMap.keys(),
  ).find(
    key =>
      prevMaintenanceStatusMap.get(key) ===
      getPrevMaintenanceStatusKeyByValue(value),
  );
  return keyFound !== undefined ? keyFound : '';
};

// Predictive Maintenance (DMM) Statuses
function getPredMaintenanceStatusKeyByValue(value: any) {
  if (isMatch('OPEN_STR', value)) return 'OPEN_STR';
  else if (isMatch('TECH_DISPATCH_REQUESTED', value))
    return 'TECH_DISPATCH_REQUESTED';
  else if (isMatch('TECH_DISPATCHED', value)) return 'TECH_DISPATCHED';
  else if (isMatch('CLOSED_STR', value)) return 'CLOSED_STR';
  else if (isMatch('DONE_BY_TECH_ENTERPRISE', value))
    return 'DONE_BY_TECH_ENTERPRISE';
  else if (isMatch('DONE_BY_TECH_DP', value)) return 'DONE_BY_TECH_DP';
  else if (isMatch('DONE_AUTOMATICALLY', value)) return 'DONE_AUTOMATICALLY';

  return 'UNKNOWN_STR';
}

const predMaintenanceStatusMap = new Map([
  [ApiEnums.PredictiveMaintenanceStatus.Unknown, 'UNKNOWN_STR'],
  [ApiEnums.PredictiveMaintenanceStatus.Open, 'OPEN_STR'],
  [
    ApiEnums.PredictiveMaintenanceStatus.TechDispatchRequested,
    'TECH_DISPATCH_REQUESTED',
  ],
  [ApiEnums.PredictiveMaintenanceStatus.TechDispatched, 'TECH_DISPATCHED'],
  [
    ApiEnums.PredictiveMaintenanceStatus.DoneByTechEnterprise,
    'DONE_BY_TECH_ENTERPRISE',
  ],
  [ApiEnums.PredictiveMaintenanceStatus.DoneByTechDP, 'DONE_BY_TECH_DP'],
  [
    ApiEnums.PredictiveMaintenanceStatus.DoneAutomatically,
    'DONE_AUTOMATICALLY',
  ],
]);

export const getPredMaintenanceStatusOptions = (): Array<string> => {
  const status = Array.from(predMaintenanceStatusMap.values());
  var localizedStatus: string[] = [];

  status.forEach(function (value: any, key: any, map: any) {
    localizedStatus.push(GetString(value));
  });

  return localizedStatus;
};

export const predMaintenanceStatusTextMap = (
  key: ApiEnums.PredictiveMaintenanceStatus,
): string => {
  let value = predMaintenanceStatusMap.get(key);
  return value ? value : '';
};

export const predMaintenanceStatusToTypeMap = (value: any) => {
  let keyFound: ApiEnums.PredictiveMaintenanceStatus | undefined = Array.from(
    predMaintenanceStatusMap.keys(),
  ).find(
    key =>
      predMaintenanceStatusMap.get(key) ===
      getPredMaintenanceStatusKeyByValue(value),
  );
  return keyFound !== undefined ? keyFound : '';
};

export const alertPriorityMap = new Map([
  [ApiEnums.IncidentPriority.Low, 'LOW_STR'],
  [ApiEnums.IncidentPriority.Medium, 'MEDIUM_STR'],
  [ApiEnums.IncidentPriority.High, 'HIGH_STR'],
  [ApiEnums.IncidentPriority.Critical, 'CRITICAL_STR'],
]);

export const getAlertPriorityOptions = (): Array<string> => {
  const alertPriorityOptions = Array.from(alertPriorityMap.values());
  var localizedAlertPriorityOptions: string[] = [];

  alertPriorityOptions.forEach(function (value: any, key: any, map: any) {
    localizedAlertPriorityOptions.push(GetString(value));
  });

  return localizedAlertPriorityOptions;
};

export const alertCategoryMap = new Map([
  [ApiEnums.AlertCategory.Weights, 'WEIGHTS'],
]);

export const alertCategoryTypeMap = new Map([
  [ApiEnums.AlertCategory.Weights, alertTypeMap],
]);

export const getAlertCategoryOptions = (): Array<string> => {
  const alertCategoryOptions = Array.from(alertCategoryMap.values());
  var localizedAlertCategoryOptions: string[] = [];

  alertCategoryOptions.forEach(function (value: any, key: any, map: any) {
    localizedAlertCategoryOptions.push(GetString(value));
  });

  return localizedAlertCategoryOptions;
};

export const getAlertTypeByCategoryOptions = (value: any) => {
  const alertTypeByCategoryOptions =
    alertCategoryTypeMap.get(value) || new Map();
  var localizedAlertTypeOptions: string[] = [];
  const alertTypeOptions = Array.from(alertTypeByCategoryOptions.values());
  alertTypeOptions.forEach(function (value: any, key: any, map: any) {
    localizedAlertTypeOptions.push(value);
  });
  return localizedAlertTypeOptions;
};

export const alertTextToCategoryMap = (value: any) => {
  let keyFound: ApiEnums.AlertCategory | undefined = Array.from(
    alertCategoryMap.keys(),
  ).find(
    key => alertCategoryMap.get(key) === getAlertCategoryKeyByValue(value),
  );
  return keyFound !== undefined ? keyFound : '';
};

function getAlertCategoryKeyByValue(value: any) {
  if (isMatch('WEIGHTS', value)) return 'WEIGHTS';

  return 'UNKNOWN_STR';
}

export const maintenanceTypeMap = new Map([
  [ApiEnums.MaintenanceType.Preventive, 'PREV_MAINT_REQUIRED'],
  [ApiEnums.MaintenanceType.Predictive, 'PRED_MAINT_REQUIRED'],
]);

export const getMaintenanceTypeOptions = (): Array<string> => {
  const maintenanceTypeOptions = Array.from(maintenanceTypeMap.values());
  var localizedMaintenanceTypeOptions: string[] = [];

  maintenanceTypeOptions.forEach(function (value: any, key: any, map: any) {
    localizedMaintenanceTypeOptions.push(GetString(value));
  });

  return localizedMaintenanceTypeOptions;
};

export const notificationFrequencyMap: Map<
  ApiEnums.NotificationFrequency | string,
  string
> = new Map([
  [ApiEnums.NotificationFrequency.Immediate, 'IMMEDIATE_STR'],
  [ApiEnums.NotificationFrequency.Daily, 'DAILY_STR'],
  [ApiEnums.NotificationFrequency.Weekly, 'WEEKLY_STR'],
  [ApiEnums.NotificationFrequency.Monthly, 'MONTHLY_STR'],
]);

export const getNotificationFrequencyOptions = (
  notificationType: string,
): Array<string> => {
  const notificationFrequencyOptions = Array.from(
    notificationFrequencyMap.entries(),
  )
    .filter(([key]) => {
      const excludeImmediate =
        notificationType === 'Reports' || notificationType === 'Health';
      return !(
        excludeImmediate && key === ApiEnums.NotificationFrequency.Immediate
      );
    })
    .map(([_, value]) => value);

  var localizedNotificationFrequencyOptions: string[] = [];

  notificationFrequencyOptions.forEach(function (
    value: any,
    key: any,
    map: any,
  ) {
    localizedNotificationFrequencyOptions.push(GetString(value));
  });

  return localizedNotificationFrequencyOptions;
};

export const frequencyTextToTypeMap = (value: any) => {
  let keyFound: ApiEnums.NotificationFrequency | string | undefined =
    Array.from(notificationFrequencyMap.keys()).find(
      key =>
        notificationFrequencyMap.get(key) === getFrequencyKeyByValue(value),
    );
  return keyFound !== undefined ? keyFound : '';
};

function getFrequencyKeyByValue(value: any) {
  if (isMatch('IMMEDIATE_STR', value)) return 'IMMEDIATE_STR';
  else if (isMatch('DAILY_STR', value)) return 'DAILY_STR';
  else if (isMatch('WEEKLY_STR', value)) return 'WEEKLY_STR';
  else if (isMatch('MONTHLY_STR', value)) return 'MONTHLY_STR';
  return 'UNKNOWN_STR';
}

export const daysOfWeekMap: Map<
  ApiEnums.DaysOfWeek | string | undefined,
  string
> = new Map([
  [ApiEnums.DaysOfWeek.Sunday, 'SUNDAY'],
  [ApiEnums.DaysOfWeek.Monday, 'MONDAY'],
  [ApiEnums.DaysOfWeek.Tuesday, 'TUESDAY'],
  [ApiEnums.DaysOfWeek.Wednesday, 'WEDNESDAY'],
  [ApiEnums.DaysOfWeek.Thursday, 'THURSDAY'],
  [ApiEnums.DaysOfWeek.Friday, 'FRIDAY'],
  [ApiEnums.DaysOfWeek.Saturday, 'SATURDAY'],
]);

export const getDaysOfWeekOptions = (): Array<string> => {
  const daysOfWeekOptions = Array.from(daysOfWeekMap.values());
  var localizedDaysOfWeekOptions: string[] = [];

  daysOfWeekOptions.forEach(function (value: any, key: any, map: any) {
    localizedDaysOfWeekOptions.push(GetString(value));
  });

  return localizedDaysOfWeekOptions;
};

export const daysTextToTypeMap = (value: any) => {
  let keyFound: ApiEnums.DaysOfWeek | string | undefined = Array.from(
    daysOfWeekMap.keys(),
  ).find(key => daysOfWeekMap.get(key) === getDaysKeyByValue(value));
  return keyFound !== undefined ? keyFound : '';
};

function getDaysKeyByValue(value: any) {
  if (isMatch('SUNDAY', value)) return 'SUNDAY';
  else if (isMatch('MONDAY', value)) return 'MONDAY';
  else if (isMatch('TUESDAY', value)) return 'TUESDAY';
  else if (isMatch('WEDNESDAY', value)) return 'WEDNESDAY';
  else if (isMatch('THURSDAY', value)) return 'THURSDAY';
  else if (isMatch('FRIDAY', value)) return 'FRIDAY';
  else if (isMatch('SATURDAY', value)) return 'SATURDAY';
  return 'UNKNOWN_STR';
}
